import React from 'react';
import { useSelector } from 'react-redux';
import { Table, Spinner, Alert } from 'react-bootstrap';
import { RootState } from 'redux/store';
import { formatTimestamp, getLocalTimezoneLabel } from 'helpers/formatDateToLocalTimezone';
import { selectHour12Option } from 'redux/selectors/userSettings';

interface StatusEntry {
  timestamp: string;
  timezone: string;
  new_status: string;
  message: string;
}

interface Props {
  entries?: StatusEntry[];
  instanceTimezone?: string | null;
  loading?: boolean;
  error?: string | null;
}

const StatusHistory: React.FC<Props> = ({ entries = [], instanceTimezone = null, loading = false, error = null }) => {
  const userData = useSelector((state: RootState) => state.auth.userData);
  const localTimezone = userData?.user_settings?.useLocalTimezone === true;
  const localTimezoneLabel = getLocalTimezoneLabel();
  const hour12 = useSelector(selectHour12Option);

  if (loading) return <Spinner animation="border" />;
  if (error) return <Alert variant="danger">{error}</Alert>;
  if (entries.length === 0) {
    return <Alert variant="info">No status history available for this instance.</Alert>;
  }

  return (
    <Table striped bordered hover responsive>
      <thead>
        <tr>
          <th>Datetime</th>
          <th>Timezone</th>
          <th>New Status</th>
          <th>Message</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((entry, index) => {
          const sourceTimezone = entry.timezone || instanceTimezone || 'UTC';

          return (
            <tr key={index}>
              <td>
                {formatTimestamp(entry.timestamp, {
                  localTimezone,
                  hour12,
                  sourceTimezone,
                  fallbackTimezone: 'UTC',
                  invalidValue: entry.timestamp,
                })}
              </td>
              <td>{localTimezone ? localTimezoneLabel : sourceTimezone}</td>
              <td>{entry.new_status}</td>
              <td>{entry.message}</td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};

export default StatusHistory;
